import { lazy, Suspense, useEffect, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { BasketProvider, useBasket } from "@/lib/basket";
import { Header } from "@/components/shop/Header";
import { Hero } from "@/components/shop/Hero";
import { Categories } from "@/components/shop/Categories";
import { TodaysFinds, OneOffFinds } from "@/components/shop/Products";
import { WhyShopaBee, Newsletter } from "@/components/shop/WhyAndNewsletter";
import { Consent } from "@/components/shop/Consent";
import { CommunityFeatured } from "@/components/community/CommunityFeatured";
import InfoPage from "@/pages/InfoPage";
import NotFound from "@/pages/NotFound";
import { CustomerProvider } from "@/lib/customer";
import { Footer } from "@/components/shop/Footer";
import Shop from "@/pages/Shop";
import ProductPage from "@/pages/ProductPage";
import { ProductsProvider, useProducts } from "@/lib/products";
import { api } from "@/lib/api";
import { useSeo } from "@/lib/seo";

// Private and admin screens load on demand to keep the public shop bundle small.
const Basket = lazy(() => import("@/pages/Basket"));
const Checkout = lazy(() => import("@/pages/Checkout"));
const OrderConfirmation = lazy(() => import("@/pages/OrderConfirmation"));
const PaymentSuccess = lazy(() => import("@/pages/PaymentResult").then((m) => ({ default: m.PaymentSuccess })));
const PaymentCancel = lazy(() => import("@/pages/PaymentResult").then((m) => ({ default: m.PaymentCancel })));
const OrderView = lazy(() => import("@/pages/OrderView"));
const Account = lazy(() => import("@/pages/account/Account"));
const AccountOrder = lazy(() => import("@/pages/account/AccountOrder"));
const Login = lazy(() => import("@/pages/account/AccountAuth").then((m) => ({ default: m.Login })));
const Register = lazy(() => import("@/pages/account/AccountAuth").then((m) => ({ default: m.Register })));
const ForgotPassword = lazy(() => import("@/pages/account/AccountAuth").then((m) => ({ default: m.ForgotPassword })));
const ResetPassword = lazy(() => import("@/pages/account/AccountAuth").then((m) => ({ default: m.ResetPassword })));
const AdminApp = lazy(() => import("@/admin/AdminApp"));
const CommunityList = lazy(() => import("@/pages/community/CommunityList"));
const AdPage = lazy(() => import("@/pages/community/AdPage"));
const PostAd = lazy(() => import("@/pages/community/PostAd"));
const SellPage = lazy(() => import("@/pages/community/SellPage"));
const MyAds = lazy(() => import("@/pages/account/MyAds"));
const ConfirmEmail = lazy(() => import("@/pages/account/ConfirmEmail"));
const GoogleCallback = lazy(() => import("@/pages/account/GoogleCallback"));
const Loading = () => <p className="max-w-7xl mx-auto px-4 py-20 text-center text-sm text-[#202A35]/60" data-testid="page-loading">Loading…</p>;

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

const TITLES = [["/shop", "Shop"], ["/basket", "Basket"], ["/checkout", "Checkout"], ["/order-confirmation", "Order confirmed"],
  ["/payment/success", "Payment"], ["/payment/cancel", "Payment cancelled"], ["/order/view", "Your order"], ["/account/login", "Log in"],
  ["/account/register", "Create account"], ["/account/forgot-password", "Forgot password"], ["/account/reset-password", "Reset password"], ["/account/confirm-email", "Confirm your email"],
  ["/account/ads", "My B Marketplace ads"], ["/community/new", "Post a free ad"], ["/account", "My account"], ["/admin", "Control Room"]];

// Public pages set their own SEO; private screens get a title and noindex.
const PRIVATE = TITLES.map(([p]) => p).filter((p) => p !== "/shop");
const PrivateSeo = ({ text }) => { useSeo({ title: text, noindex: true }); return null; };
const PathTitle = () => {
  const { pathname } = useLocation();
  const hit = TITLES.find(([p]) => PRIVATE.includes(p) && pathname.startsWith(p));
  return hit ? <PrivateSeo text={hit[1]} /> : null;
};

// Anonymous, cookie-free page counter: sessionStorage only marks the first page of a tab session.
const VisitCounter = () => {
  const { pathname } = useLocation();
  const last = useRef(null);
  useEffect(() => {
    if (last.current === pathname) return; // StrictMode re-runs effects in development
    last.current = pathname;
    let first = false;
    try { first = !sessionStorage.getItem("sb-visit"); sessionStorage.setItem("sb-visit", "1"); } catch { /* storage blocked */ }
    api.post("/visits", { new_visit: first }).catch(() => {});
  }, [pathname]);
  return null;
};

const Home = ({ onAdd }) => {
  const { products } = useProducts();
  const fresh = products.filter((p) => !p.oneOff).slice(0, 10);
  const oneOff = products.filter((p) => p.oneOff).slice(0, 3);
  useSeo({ title: "ShopaBee | New, Open-Box & Pre-Loved Bargains in the UK", path: "/",
    description: "Discover new, open-box, pre-loved and clearance bargains at ShopaBee. Honest condition notes, clear prices in pounds and delivery across the UK." });
  return (
    <>
      <Hero />
      <Categories onAdd={onAdd} />
      <TodaysFinds onAdd={onAdd} items={fresh} />
      <OneOffFinds onAdd={onAdd} items={oneOff} />
      <CommunityFeatured />
      <WhyShopaBee />
      <Newsletter />
    </>
  );
};

const Store = () => {
  const { add: onAdd, count } = useBasket();
  const { hash } = useLocation();
  if (hash.includes("session_id=")) return <Suspense fallback={<Loading />}><GoogleCallback /></Suspense>; // Google login return, before any routes
  return (
    <>
      <VisitCounter />
      <Header basketCount={count} />
      <main>
        <Suspense fallback={<Loading />}>
        <Routes>
          <Route path="/" element={<Home onAdd={onAdd} />} />
          <Route path="/shop" element={<Shop onAdd={onAdd} />} />
          <Route path="/shop/:category" element={<Shop onAdd={onAdd} />} />
          <Route path="/community" element={<CommunityList />} />
          <Route path="/community/new" element={<PostAd />} />
          <Route path="/community/sell" element={<SellPage />} />
          <Route path="/community/ad/:id" element={<AdPage />} />
          <Route path="/community/ad/:id/edit" element={<PostAd />} />
          <Route path="/account/ads" element={<MyAds />} />
          <Route path="/product/:id" element={<ProductPage onAdd={onAdd} />} />
          <Route path="/basket" element={<Basket />} />
          <Route path="/checkout" element={<Checkout />} />
          <Route path="/order-confirmation" element={<OrderConfirmation />} />
          <Route path="/payment/success" element={<PaymentSuccess />} />
          <Route path="/payment/cancel" element={<PaymentCancel />} />
          <Route path="/order/view/:token" element={<OrderView />} />
          <Route path="/info/:slug" element={<InfoPage />} />
          <Route path="/account" element={<Account />} />
          <Route path="/account/login" element={<Login />} />
          <Route path="/account/register" element={<Register />} />
          <Route path="/account/forgot-password" element={<ForgotPassword />} />
          <Route path="/account/reset-password" element={<ResetPassword />} />
          <Route path="/account/confirm-email" element={<ConfirmEmail />} />
          <Route path="/account/orders/:id" element={<AccountOrder />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
        </Suspense>
      </main>
      <Footer />
      <Consent />
    </>
  );
};

function App() {
  return (
    <div id="top" className="App min-h-screen bg-white text-[#202A35]">
      <BrowserRouter>
        <ScrollToTop />
        <PathTitle />
        <Routes>
          <Route path="/admin/*" element={<Suspense fallback={<Loading />}><AdminApp /></Suspense>} />
          <Route path="*" element={<CustomerProvider><ProductsProvider><BasketProvider><Store /></BasketProvider></ProductsProvider></CustomerProvider>} />
        </Routes>
      </BrowserRouter>
      <Toaster position="bottom-right" toastOptions={{ style: { fontFamily: "Figtree, sans-serif" } }} />
    </div>
  );
}

export default App;
